/**
 * `/playlists/:playlistId` — the playlist detail screen.
 *
 * TASK 9.3 built the playlists LIST (PlaylistsList.js) and the create/rename/
 * delete modals (PlaylistModals.js) but no DETAIL view, so the composition root
 * had no `playlist` branch and `/playlists/:id` fell through to a heading-only
 * stub (the playlists e2e spec's "the detail VIEW is unwired" note). This is the
 * §3-aligned detail surface that makes the route reachable AND completes the
 * membership mutations AC 9.3 requires: over one cursor PAGE of track rows wired
 * to `GET /api/playlists/:id` (TASK 6.2) it exposes the `/playlists/:id`
 * controls the contract enumerates — the All-playlists link, Play / Shuffle,
 * Rename, Delete — PLUS per-row REORDER (↑/↓ arrows and a move-to-position-N
 * picker) and per-row REMOVE. Rename/Delete ride the SAME OverlayStack modals
 * the list uses (PlaylistModals.js); the per-row ⋯ sheet + move picker ride the
 * SAME OverlayStack primitives (overlays.js#ActionSheet + the queue's
 * MoveToPositionSheet.js), so they inherit the focus trap and Escape/back
 * dismissal.
 *
 * ── THE QUEUE IDIOM, MINUS A VERSION PARAM (read this before touching a mutation)
 * QueueView.js reorders/removes against an OPTIMISTIC-CONCURRENCY queue: every
 * op carries a `version` and a stale one answers 409, which sends the view
 * through the reconcile walk. The playlist membership API is DIFFERENT: it is
 * LAST-WRITE-WINS (`playlistSummary.updatedAt` is the LWW token, AC 9.7) and
 * `POST /reorder` / `DELETE /tracks/:itemId` take NO version and never answer
 * 409 (routes/playlists.ts: reorder is 200 | 404 | 422, remove is 200 | 404 |
 * 422). So "version discipline" here reduces to ADOPTING server truth after
 * every mutation — reorder confirms then QUIET-REFRESHES the page (the display
 * is the refresh's truth, never a local splice, exactly as QueueView) — with a
 * defensive CONFLICT branch that reconciles the same way should the contract
 * ever grow one. REMOVE is the FavoritesView.js idiom instead: an optimistic
 * splice that rolls back with an announced toast on failure, since a 200 there
 * carries nothing the view needs and a 404 IS success (the row is already gone).
 *
 * ── OFFLINE-AWARE, AND THE REORDER/PLAY FILTERS NEVER DISAGREE ─────────────
 * `stateOfTrack` is the SINGLE availability oracle: a gone / format-unsupported
 * / offline-and-not-downloaded track is a non-null marker. Play/Shuffle exclude
 * exactly those (playableTracks), a blocked row's tap surfaces the §10.6 copy
 * instead of streaming, AND its reorder controls are disabled — an offline
 * non-downloaded row is non-reorderable, never feeding an unplayable move to the
 * server. REMOVE stays available on a blocked row: cleaning a dead entry out of
 * a playlist is exactly what you want to do to it.
 *
 * ── LOADING / EMPTY / ERROR live in the BODY (§10.6) ──────────────────────
 * The head (heading + controls) survives a failed or empty load; only `body`
 * swaps between skeleton, retriable error, empty strip and the track list, and
 * a failed "load more" (or a failed quiet refresh) keeps the loaded rows on
 * screen with its own retry in `notice`.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside this file's task */

import {
  ERROR_CODES,
  PLAYLIST_TRACKS_MAX,
  TESTIDS,
  actionsForButton,
  moveReorderButton,
  playTrackButton,
} from '@streamymusic/shared';

import { ApiError, fieldErrorText } from '../../../api/api-client.js';
import { ActionSheet } from '../../app/overlays.js';
import {
  AVAILABILITY_COPY,
  AvailabilityBadge,
  availabilityOpacity,
  availabilityState,
  isPlayable,
} from '../../components/AvailabilityBadge.js';
import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  RETRIABLE_ERROR_MESSAGE,
} from '../../components/StateBlocks.js';
import { append, el } from '../../components/primitives/dom.js';
import { formatSeconds } from '../../components/primitives/format.js';

import { MoveToPositionSheet, moveToPositionDirective } from '../queue/MoveToPositionSheet.js';

import { openDeletePlaylistModal, openRenamePlaylistModal } from './PlaylistModals.js';

/** `GET /api/playlists/:id` clamps a page at <= 200 (dto.ts `PAGE_SIZE_MAX`). */
export const PLAYLIST_DETAIL_PAGE_SIZE = 200;

/** AC 9.6 — the empty-playlist copy. */
export const EMPTY_PLAYLIST_HEADLINE = 'NO TRACKS YET';
export const EMPTY_PLAYLIST_MESSAGE =
  'This mix is empty — add tracks from any album or song to fill it up.';

/**
 * Announced while a (defensive) 409, or a 404 that means "the row moved under
 * us", sends the view through a quiet refresh — the playlist's stand-in for the
 * queue's reconcile walk (see the module header on why there is no 409 today).
 */
export const PLAYLIST_CONFLICT_MESSAGE = 'This playlist changed on another device — refreshing.';

/** Announced + toasted when an optimistic remove is rolled back (AC 10.3 sibling). */
export const PLAYLIST_REMOVE_FAILED_MESSAGE = 'Couldn’t remove the track — putting it back.';

/**
 * @typedef {object} PlaylistDetailHandlers
 * @property {(tracks: readonly import('@streamymusic/shared').TrackCard[], index: number,
 *   shuffle: boolean) => void} onPlay PLAYABLE tracks only, in list order — the
 *   composition root owns the transport and the playlist play context.
 * @property {(message: string, context: { state: string, trackId: string }) => void} onBlocked
 *   a non-playable row tap (§10.6 per-state copy).
 * @property {() => void} onDeleted the playlist was deleted — the root navigates
 *   away (this view must not import the router).
 */

/**
 * Builds the `/playlists/:playlistId` section and starts loading its first page.
 *
 * @param {{
 *   api: Pick<import('../../../api/api-client.js').ApiClient,
 *     'getPlaylist'|'renamePlaylist'|'deletePlaylist'|'removePlaylistTrack'|'reorderPlaylist'>,
 *   heading: HTMLElement,
 *   playlistId: string,
 *   overlays: import('../../app/OverlayStack.js').OverlayStackHandle,
 *   handlers: PlaylistDetailHandlers,
 *   offline?: boolean,
 *   downloadedTracks?: readonly string[],
 *   pageSize?: number,
 *   announce?: (text: string) => void,
 *   notify?: (message: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` (router.js
 *   `PageHeading`) — the shell contract requires exactly one per route, so the
 *   view is handed it and fills in the playlist name once loaded. `overlays` is
 *   the shell's stack; the rename/delete modals AND the per-row ⋯ sheet /
 *   move-to-position picker MUST go through it. `notify` is the shell's toast
 *   seam, `announce` its polite live region, `warn` the app's diagnostics seam
 *   for a server contract violation (an `available=false` row on a retain
 *   surface is allowed; the seam mirrors the other views' shape).
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function PlaylistDetail(options) {
  const {
    api,
    heading,
    playlistId,
    overlays,
    handlers,
    offline = false,
    downloadedTracks = [],
    pageSize = PLAYLIST_DETAIL_PAGE_SIZE,
    announce,
    notify = () => {},
    warn = (message) => {
      console.warn(message);
    },
  } = options;

  const downloadedTrackIds = new Set(downloadedTracks);

  const section = el('section', { attrs: { 'data-screen-label': 'Playlist detail' } });

  /* ── head: back link + heading + play/shuffle + rename/delete ───────────── */

  const backLink = el('a', {
    className: 'sm-link sm-link--back',
    text: '‹ All playlists',
    testId: TESTIDS.ALL_PLAYLISTS_LINK,
    attrs: { href: '#/playlists' },
  });

  const playButton = el('button', {
    className: 'sm-btn sm-btn--primary sm-btn--display',
    text: '▶ PLAY',
    testId: TESTIDS.PLAY_BUTTON,
    attrs: { type: 'button', disabled: true },
    on: { click: () => startPlay(false) },
  });
  const shuffleButton = el('button', {
    className: 'sm-btn sm-btn--accent sm-btn--display',
    text: '⤨ SHUFFLE',
    testId: TESTIDS.SHUFFLE_BUTTON,
    attrs: { type: 'button', disabled: true },
    on: { click: () => startPlay(true) },
  });
  const renameButton = el('button', {
    className: 'sm-btn sm-btn--secondary-13 sm-btn--panel',
    text: 'Rename',
    testId: TESTIDS.RENAME_BUTTON,
    attrs: { type: 'button', disabled: true },
    on: { click: () => openRename() },
  });
  const deleteButton = el('button', {
    className: 'sm-btn sm-btn--secondary-13 sm-btn--panel-danger',
    text: 'Delete',
    testId: TESTIDS.DELETE_BUTTON,
    attrs: { type: 'button', disabled: true },
    on: { click: () => openDelete() },
  });

  const controls = append(
    el('div', { className: 'sm-actions' }),
    playButton,
    shuffleButton,
    renameButton,
    deleteButton,
  );

  const countLabel = el('p', { className: 'sm-count-label', attrs: { hidden: true } });
  const list = el('div', { className: 'sm-stack-8' });
  const body = el('div');
  /** A FAILED "load more" / quiet refresh, and a mutation error, land here —
   *  the loaded rows must survive them. */
  const notice = el('div');
  append(section, backLink, heading, controls, countLabel, body, notice);

  /* ── state ──────────────────────────────────────────────────────────────── */

  /** @type {import('@streamymusic/shared').PlaylistTrackEntry[]} */
  let entries = [];
  /** @type {string|null} */
  let cursor = null;
  /** Server-truth total across all pages (dto.ts `trackCount`). */
  let trackCount = 0;
  let name = '';
  /** Discards an out-of-order settle (a Retry racing the request it retried). */
  let generation = 0;
  let disposed = false;
  /** @type {AbortController|null} */
  let controller = null;
  /** One membership mutation at a time — LWW means concurrent writes race. */
  let mutating = false;
  /**
   * Keyboard continuity across the repaint a reorder forces: the control to
   * re-focus, keyed by the item it belonged to (testids shift with ordinals,
   * items do not).
   *
   * @type {{ itemId: string, control: 'up'|'down'|'more' }|null}
   */
  let pendingFocus = null;
  /** @type {Map<string, { up: HTMLElement, down: HTMLElement, more: HTMLElement, play: HTMLElement }>} */
  const rowsByItemId = new Map();

  /* The playlist's position space is trivial next to the queue's: rows are a
   * contiguous 0-based `0..trackCount-1`, always loaded FROM THE START, so the
   * first loaded row is absolute position 0 and a loaded row's display slot is
   * simply its index — no played prefix, no playing item to reason around. */
  const FIRST_POSITION = 0;

  /**
   * The retain-surface availability state for a track (gone / format-unsupported
   * / offline-and-not-downloaded → a non-null marker; null = playable AND
   * reorderable).
   * @param {import('@streamymusic/shared').PlaylistTrackEntry['track']} track
   */
  function stateOfTrack(track) {
    return availabilityState({
      available: track.available,
      formatSupported: track.formatSupported,
      offline,
      downloaded: downloadedTrackIds.has(track.id),
      kind: 'track',
      surface: 'playlist',
      onScopeViolation: (message) => warn(message, { surface: 'playlist', trackId: track.id }),
    });
  }

  /** Playable tracks, in list order — offline-aware (AC 10.2 / 13.5): a gone,
   *  format-unsupported, OR offline-and-not-downloaded track is excluded, so
   *  Play/Shuffle never feed an unplayable track to the transport while its row
   *  is blocked. */
  function playableTracks() {
    return entries
      .map((entry) => entry.track)
      .filter((track) => isPlayable(stateOfTrack(track)));
  }

  /** @param {boolean} shuffle */
  function startPlay(shuffle) {
    const playable = playableTracks();
    if (playable.length === 0) return;
    handlers.onPlay(playable, 0, shuffle);
  }

  function openRename() {
    openRenamePlaylistModal({
      overlays,
      api,
      playlist: { id: playlistId, name },
      onRenamed: (summary) => {
        if (disposed) return;
        name = summary.name;
        heading.textContent = summary.name;
        notify(`Renamed to “${summary.name}”`);
      },
    });
  }

  function openDelete() {
    openDeletePlaylistModal({
      overlays,
      api,
      playlist: { id: playlistId, name },
      onDeleted: () => {
        notify(`Playlist “${name}” deleted`);
        handlers.onDeleted();
      },
      onError: (message) => notify(message),
    });
  }

  /**
   * One track row (favorites' SongRow idiom — a container with an inner Play
   * button so the reorder ↑/↓ + ⋯ controls can sit beside it; a whole-row
   * button could not nest them). A blocked row surfaces §10.6 copy on tap and
   * disables its reorder controls (see the module header).
   *
   * @param {import('@streamymusic/shared').PlaylistTrackEntry} entry
   * @param {number} index 0-based position in the loaded list
   * @returns {HTMLElement}
   */
  function TrackRow(entry, index) {
    const { track } = entry;
    // `surface:'playlist'` is a RETAIN surface — a gone track keeps its marker.
    // Same offline-aware state used by playableTracks(), so row-block,
    // Play/Shuffle-eligibility and reorder-eligibility never disagree.
    const marker = stateOfTrack(track);
    const reorderable = marker === null;
    const ordinal = index + 1;

    const row = el('div', { className: 'sm-track-row sm-track-row--gap10' });

    const play = el('button', {
      className: 'sm-track-row__play',
      testId: playTrackButton(track.title),
      attrs: { type: 'button', 'aria-label': `Play ${track.title}` },
      styleProps: { opacity: String(availabilityOpacity(marker)) },
      on: {
        click: () => {
          if (marker !== null) {
            handlers.onBlocked(AVAILABILITY_COPY[marker].banner, {
              state: marker,
              trackId: track.id,
            });
            return;
          }
          const playable = playableTracks();
          const playIndex = Math.max(
            0,
            playable.findIndex((candidate) => candidate.id === track.id),
          );
          handlers.onPlay(playable, playIndex, false);
        },
      },
    });

    const textCell = el('span', { className: 'sm-flex-fill' });
    append(
      textCell,
      el('span', { className: 'sm-line-primary', text: track.title }),
      el('span', {
        className: 'sm-line-secondary',
        text: `${track.artistName} — ${track.albumTitle}`,
      }),
      marker === null ? null : AvailabilityBadge({ state: marker }),
    );
    append(
      play,
      el('span', { className: 'sm-row__play-glyph', text: '▶', attrs: { 'aria-hidden': 'true' } }),
      textCell,
      el('span', { className: 'sm-line-trailing', text: formatSeconds(track.durationS) }),
    );

    // ↑ is a no-op at the very top (already position 0); ↓ is a no-op only when
    // this is the last row of a fully-loaded list. A blocked row disables both.
    const atTop = index === 0;
    const atBottom = index === entries.length - 1 && cursor === null;
    const up = el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-narrow',
      text: '↑',
      testId: moveReorderButton('up', ordinal),
      attrs: {
        type: 'button',
        'aria-label': 'Move up',
        ...(reorderable && !atTop ? {} : { disabled: true }),
      },
      on: {
        click: () => {
          if (!reorderable || atTop) return;
          void reorder({ kind: 'up' }, entry.itemId, { itemId: entry.itemId, control: 'up' });
        },
      },
    });
    const down = el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-narrow',
      text: '↓',
      testId: moveReorderButton('down', ordinal),
      attrs: {
        type: 'button',
        'aria-label': 'Move down',
        ...(reorderable && !atBottom ? {} : { disabled: true }),
      },
      on: {
        click: () => {
          if (!reorderable || atBottom) return;
          void reorder({ kind: 'down' }, entry.itemId, { itemId: entry.itemId, control: 'down' });
        },
      },
    });
    const more = el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-muted',
      text: '⋯',
      testId: actionsForButton(track.title),
      attrs: { type: 'button', 'aria-label': `Actions for ${track.title}` },
      on: { click: () => openRowActions(entry, index, reorderable) },
    });

    append(row, play, up, down, more);
    rowsByItemId.set(entry.itemId, { up, down, more, play });
    return row;
  }

  /** The honest count line — the server's total (dto.ts `trackCount`). */
  function paintCount() {
    countLabel.textContent = `${trackCount.toLocaleString()} track${trackCount === 1 ? '' : 's'}`;
    countLabel.removeAttribute('hidden');
  }

  function paintRows() {
    rowsByItemId.clear();
    list.replaceChildren(...entries.map((entry, index) => TrackRow(entry, index)));
    if (cursor !== null) list.append(loadMoreButton);
    if (body.firstChild !== list) body.replaceChildren(list);
    enableControls();
    restoreFocus();
  }

  /** Rows when there are any, otherwise the §10.6 empty strip — the body is
   *  never blank once a load has settled. */
  function paintBody() {
    if (entries.length === 0) {
      body.replaceChildren(
        EmptyBlock({
          headline: EMPTY_PLAYLIST_HEADLINE,
          message: EMPTY_PLAYLIST_MESSAGE,
          ...(announce === undefined ? {} : { announce }),
        }),
      );
      enableControls();
      restoreFocus();
      return;
    }
    paintRows();
  }

  /**
   * Puts focus back on the control the last reorder was issued from — the
   * repaint rebuilt every row, and dropping a keyboard user back to `<body>`
   * after each "Move up" would make the accessible path unusable in practice.
   */
  function restoreFocus() {
    if (pendingFocus === null) return;
    const focus = pendingFocus;
    pendingFocus = null;
    const controlsForItem = rowsByItemId.get(focus.itemId);
    if (controlsForItem === undefined) return;
    controlsForItem[focus.control]?.focus({ preventScroll: true });
  }

  const loadMoreButton = el('button', {
    className: 'sm-btn sm-btn--block',
    text: 'Load more tracks ↓',
    testId: TESTIDS.LOAD_MORE_BUTTON,
    attrs: { type: 'button' },
    on: {
      click: () => {
        void load({ append: true });
      },
    },
  });

  /** Head controls only become live once the header data has loaded. */
  function enableControls() {
    renameButton.removeAttribute('disabled');
    deleteButton.removeAttribute('disabled');
    const hasPlayable = playableTracks().length > 0;
    for (const button of [playButton, shuffleButton]) {
      if (hasPlayable) button.removeAttribute('disabled');
      else button.setAttribute('disabled', '');
    }
  }

  /* ── mutations ────────────────────────────────────────────────────────────
   *
   * REORDER (↑/↓ + move-to-N): the QueueView idiom minus the version param —
   * apply on the server, then QUIET-REFRESH so the display is server truth, not
   * a local splice. REMOVE: the FavoritesView idiom — optimistic splice that
   * rolls back with an announced toast on failure. Both are serialised by
   * `mutating` because a LWW endpoint races concurrent writes. */

  /** @param {string} text */
  function surfaceActionError(text, opts = {}) {
    notice.replaceChildren(el('p', { className: 'sm-form-error', text, attrs: { role: 'alert' } }));
    if (announce !== undefined) announce(text);
    if (opts.toast === true) notify(text);
  }

  /**
   * Applies one reorder directive, then re-reads the page (renumbered
   * positions are the server's to hand back). Adopts `trackCount` from the
   * confirming summary so the count is right even if the refresh then fails.
   *
   * @param {import('@streamymusic/shared').MoveDirective} move
   * @param {string} itemId
   * @param {{ itemId: string, control: 'up'|'down'|'more' }} [focusMemo]
   * @returns {Promise<void>}
   */
  async function reorder(move, itemId, focusMemo) {
    if (mutating || disposed) return;
    mutating = true;
    const keep = entries.length;
    try {
      const summary = await api.reorderPlaylist(playlistId, itemId, move);
      if (disposed) return;
      trackCount = summary.trackCount;
      pendingFocus = focusMemo ?? null;
      await load({ quiet: true, keep });
    } catch (error) {
      if (disposed) return;
      const code = /** @type {{ code?: string }} */ (error).code;
      if (code === ERROR_CODES.CONFLICT) {
        // Defensive: the LWW endpoint does not 409 today (module header), but if
        // it ever does, reconcile exactly as the queue would — refresh, never
        // re-apply blindly.
        if (announce !== undefined) announce(PLAYLIST_CONFLICT_MESSAGE);
        await load({ quiet: true, keep });
        return;
      }
      if (code === ERROR_CODES.NOT_FOUND) {
        // The row (or playlist) is not ours any more — refresh the page.
        surfaceActionError(RETRIABLE_ERROR_MESSAGE);
        await load({ quiet: true, keep });
        return;
      }
      // A malformed directive (422) — surface any actionable fieldErrors text;
      // the rows it refers to stay on screen.
      surfaceActionError(
        fieldErrorText(error) ??
          (error instanceof ApiError ? error.message : RETRIABLE_ERROR_MESSAGE),
      );
    } finally {
      mutating = false;
    }
  }

  /**
   * Removes one row optimistically (the gap closes at once), confirms against
   * the server, and rolls the row back — with an announced toast — if the
   * server refuses for a reason other than "already gone".
   *
   * @param {string} itemId
   * @returns {Promise<void>}
   */
  async function removeTrack(itemId) {
    if (mutating || disposed) return;
    const index = entries.findIndex((entry) => entry.itemId === itemId);
    if (index === -1) return;
    mutating = true;
    const removed = entries[index];
    // Optimistic: drop the row NOW; index-based ordinals renumber for free.
    entries = entries.filter((entry) => entry.itemId !== itemId);
    trackCount = Math.max(0, trackCount - 1);
    paintCount();
    paintBody();
    try {
      await api.removePlaylistTrack(playlistId, itemId);
      if (disposed) return;
      // 200 confirms the optimistic removal — nothing left to reconcile.
    } catch (error) {
      if (disposed) return;
      const code = /** @type {{ code?: string }} */ (error).code;
      if (code === ERROR_CODES.NOT_FOUND) {
        // Already absent for this caller — the remove SUCCEEDED (the DELETE
        // 404-is-success contract). Quiet-refresh to reconcile server truth.
        if (announce !== undefined) announce(PLAYLIST_CONFLICT_MESSAGE);
        await load({ quiet: true, keep: entries.length });
        return;
      }
      // Any other failure (transport / 5xx): put the row back where it was.
      const restored = [...entries];
      restored.splice(index, 0, removed);
      entries = restored;
      trackCount += 1;
      paintCount();
      paintBody();
      surfaceActionError(PLAYLIST_REMOVE_FAILED_MESSAGE, { toast: true });
    } finally {
      mutating = false;
    }
  }

  /* ── overlays ─────────────────────────────────────────────────────────────
   *
   * The per-row ⋯ sheet (ui-contract §10.3 shape) + the move-to-position-N
   * picker reused from the queue. §10.3: each item closes the sheet, THEN acts. */

  /**
   * @param {import('@streamymusic/shared').PlaylistTrackEntry} entry
   * @param {number} index 0-based visible index at open time
   * @param {boolean} reorderable a blocked row offers Remove only
   */
  function openRowActions(entry, index, reorderable) {
    const { track } = entry;
    /** @param {() => void} run */
    const act = (run) => () => {
      overlays.closeTop({ then: run });
    };
    /** @type {import('../../app/overlays.js').SheetItem[]} */
    const items = [];
    if (reorderable) {
      items.push(
        {
          icon: '↑',
          label: 'Move to top',
          onSelect: act(() => {
            void reorder({ kind: 'top' }, entry.itemId, { itemId: entry.itemId, control: 'more' });
          }),
        },
        {
          icon: '↓',
          label: 'Move to bottom',
          onSelect: act(() => {
            void reorder({ kind: 'bottom' }, entry.itemId, {
              itemId: entry.itemId,
              control: 'more',
            });
          }),
        },
        {
          icon: '#',
          label: 'Move to position…',
          onSelect: act(() => openMoveToPosition(entry, index)),
        },
      );
    }
    items.push({
      icon: '✕',
      label: 'Remove from playlist',
      color: 'var(--sm-color-pink)',
      onSelect: act(() => {
        void removeTrack(entry.itemId);
      }),
    });
    overlays.open(
      ActionSheet({
        title: track.title,
        onDismissRequest: () => overlays.closeTop(),
        items,
      }),
    );
  }

  /**
   * @param {import('@streamymusic/shared').PlaylistTrackEntry} entry
   * @param {number} index
   */
  function openMoveToPosition(entry, index) {
    overlays.open(
      MoveToPositionSheet({
        title: entry.track.title,
        position: index + 1,
        // The whole playlist is addressable (the server clamps a target past
        // the end), so `total` is the server-truth count, not the loaded slice.
        total: trackCount,
        onSubmit: (displayPosition) => {
          overlays.closeTop({
            then: () => {
              void reorder(
                // Positions are 0-based from the top (no played prefix), so
                // display slot N maps to absolute position N-1.
                moveToPositionDirective(displayPosition, FIRST_POSITION),
                entry.itemId,
                { itemId: entry.itemId, control: 'more' },
              );
            },
          });
          return null;
        },
        onCancel: () => overlays.closeTop(),
      }),
    );
  }

  /* ── loading ──────────────────────────────────────────────────────────────
   *
   * `append` fetches the next display page onto what is held. `quiet` refreshes
   * in place after a mutation — no skeleton, rows stay up until the fresh data
   * lands. `keep` asks the fresh walk to cover at least that many rows so a
   * grown (load-more'd) window survives the refresh. */

  /**
   * @param {{ append?: boolean, quiet?: boolean, keep?: number }} [opts]
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const appendPage = opts.append === true;
    const quiet = opts.quiet === true;
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    if (appendPage) {
      loadMoreButton.setAttribute('disabled', '');
      loadMoreButton.textContent = 'Loading…';
    } else if (!quiet) {
      entries = [];
      cursor = null;
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 6, label: 'Loading playlist' }));
    }

    try {
      if (appendPage) {
        const page = await api.getPlaylist(playlistId, {
          limit: pageSize,
          ...(cursor !== null ? { cursor } : {}),
          signal: ownController.signal,
        });
        if (disposed || own !== generation) return;
        name = page.name;
        heading.textContent = page.name;
        trackCount = page.trackCount;
        entries = [...entries, ...page.items];
        cursor = page.nextCursor;
      } else {
        // Fresh / quiet: walk display pages FROM THE START until the requested
        // window is covered (a plain fresh load covers exactly one page).
        const needed = Math.max(opts.keep ?? 0, pageSize);
        /** @type {import('@streamymusic/shared').PlaylistTrackEntry[]} */
        let fresh = [];
        /** @type {string|null} */
        let freshCursor = null;
        let safety = Math.ceil(PLAYLIST_TRACKS_MAX / pageSize) + 1;
        do {
          const page = await api.getPlaylist(playlistId, {
            limit: pageSize,
            ...(freshCursor === null ? {} : { cursor: freshCursor }),
            signal: ownController.signal,
          });
          if (disposed || own !== generation) return;
          name = page.name;
          heading.textContent = page.name;
          trackCount = page.trackCount;
          fresh = [...fresh, ...page.items];
          freshCursor = page.nextCursor;
          safety -= 1;
        } while (freshCursor !== null && fresh.length < needed && safety > 0);
        entries = fresh;
        cursor = freshCursor;
      }

      loadMoreButton.removeAttribute('disabled');
      loadMoreButton.textContent = 'Load more tracks ↓';
      paintCount();
      paintBody();
    } catch (error) {
      if (disposed || own !== generation) return;
      const code = /** @type {{ code?: string }} */ (error).code;
      if (appendPage && code === ERROR_CODES.VALIDATION) {
        // A tampered/expired cursor — start over rather than retry the bad one.
        void load();
        return;
      }
      const retry = ErrorBlock({
        onRetry: () => {
          void load(appendPage ? { append: true } : {});
        },
        ...(announce === undefined ? {} : { announce }),
      });
      if (appendPage) {
        loadMoreButton.removeAttribute('disabled');
        loadMoreButton.textContent = 'Load more tracks ↓';
        notice.replaceChildren(retry);
      } else if (quiet) {
        // A failed refresh keeps the rows already on screen (§10.6).
        notice.replaceChildren(retry);
      } else {
        body.replaceChildren(retry);
      }
    }
  }

  void load();

  return {
    element: section,
    dispose() {
      disposed = true;
      controller?.abort();
    },
  };
}
